import React, { useCallback, useEffect } from 'react';
import { NumberConfig } from '@kemu-io/kemu-core/types';
import variablesManager, { getControlScopedVarName } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import classNames from 'classnames';
import { DataType } from '@kemu-io/hs-types';
import FieldLabel from '../FieldLabel/FieldLabel';
import styles from './NumericField.module.css';
import StyledNumericInput from '@components/form-control/StyledNumericInput/StyledNumericInput';
import FormGroup from '@components/form-control/formGroup/formGroup';
import { NO_GLOBAL_RULES_CLASS } from '@common/constants';
import useForceUpdate from '@hooks/useForceReload';

type Props = {
  label: string;
  variableName: string;
  recipeId: string;
  thingRecipeId: string;
  widgetId: string;
  color?: string;
  /** indicates this is the only field in the widget */
  isSingleElement: boolean;
  config: NumberConfig;
  settingsIndex: number;
  onUpdateConfig: (settingsIndex: number, newConfig: NumberConfig) => void;
}

const NumericField = (props: Props) => {
  const { variableName, widgetId, label, isSingleElement, settingsIndex, onUpdateConfig, config } = props;
  const scopedVarName = getControlScopedVarName(variableName);
  const forceUpdate = useForceUpdate();

  const variableValue = variablesManager.getVariableValue({
    variableName: scopedVarName,
    readerWidgetId: widgetId,
  });

  const handleValueChange = useCallback((value: number) => {
    variablesManager.setVariableValue({
      variableName: scopedVarName,
      setByWidgetId: widgetId,
      type: DataType.Number,
      value,
    });

    onUpdateConfig(settingsIndex, {
      ...config,
      customValue: value,
    } as NumberConfig);
  }, [scopedVarName, widgetId, onUpdateConfig, settingsIndex, config]);

  // Subscribe to variable changes to trigger re-render
  useEffect(() => {
    const unsubscribe = variablesManager.onVariableValueChange({
      variableName: scopedVarName,
      type: DataType.Anything,
      widgetId: widgetId,
      onValueChange: forceUpdate,
    });

    return unsubscribe;
  }, [scopedVarName, widgetId, forceUpdate]);

  return (
    <FormGroup column={true} noMarginBottom>
      <FieldLabel color={props.color} label={label} />
      <StyledNumericInput
        value={variableValue?.value as number ?? config.customValue ?? config.defaultValue ?? 0}
        placeholder={config.placeholder}
        onValueChange={handleValueChange}
        inputClassName={NO_GLOBAL_RULES_CLASS}
        className= {classNames({
          [styles.SingleElement]: isSingleElement && !label
        })}
      />
    </FormGroup>
  );
};

export default NumericField;
